import BusinessProfilesTab from "./BusinessProfilesTab";
import ApiWebhooksTab from "./ApiWebhooksTab";
import RecLibraryTab from "./RecLibraryTab";
import { Briefcase } from "lucide-react";

import React, { useState, useEffect, useRef } from "react";
import { api } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import {
  Settings, Users, BookOpen, Plus, Trash2, Shield, FileText,
  CheckCircle, AlertCircle, Save, X, Upload, RefreshCw, Brain, ToggleLeft, ToggleRight,
  Image as ImageIcon, GripVertical, ChevronDown, BarChart2, TrendingUp, DollarSign, Mail, Copy,
  ClipboardList, MapPin, UserPlus, Zap, GitBranch
} from "lucide-react";

const INSPECTION_TYPES = [
  { value: "all", label: "All Types" },
  { value: "commercial_property", label: "Commercial Property" },
  { value: "workers_comp", label: "Workers' Compensation" },
  { value: "habitational", label: "Habitational" },
  { value: "general_liability", label: "General Liability" },
  { value: "commercial_auto", label: "Commercial Auto" },
];

const TYPE_LABELS = {
  commercial_property: "Commercial Property",
  workers_comp: "Workers' Comp",
  habitational: "Habitational",
  general_liability: "General Liability",
  commercial_auto: "Commercial Auto",
};

const SEV_COLORS = {
  Critical: "bg-red-100 text-red-700 border-red-200",
  High: "bg-orange-100 text-orange-700 border-orange-200",
  Medium: "bg-amber-100 text-amber-700 border-amber-200",
  Low: "bg-green-100 text-green-700 border-green-200",
};

const SOURCE_LABELS = {
  ai_approved: "AI Approved",
  ai_edited: "Edited & Approved",
  manually_added: "Manually Added",
};

function GuidelinesTab() {
  const [guidelines, setGuidelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ filename: "", inspection_type: "all", content: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/carrier/guidelines");
      setGuidelines(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("filename", form.filename);
      fd.append("inspection_type", form.inspection_type);
      fd.append("content", form.content);
      await api.post("/carrier/guidelines", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAdding(false);
      setForm({ filename: "", inspection_type: "all", content: "" });
      load();
    } catch {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await api.delete(`/carrier/guidelines/${id}`);
    setGuidelines(prev => prev.filter(g => g.id !== id));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-semibold text-slate-700">Underwriting Guidelines</h3>
          <p className="text-sm text-slate-500 mt-0.5">Upload carrier-specific guidelines to train the AI survey engine</p>
        </div>
        <button onClick={() => setAdding(true)} data-testid="add-guideline-btn"
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors">
          <Plus className="w-4 h-4" /> Add Guidelines
        </button>
      </div>

      {adding && (
        <form onSubmit={handleSave} className="bg-navy/5 border border-navy/20 rounded-xl p-5 space-y-4 animate-slide-up">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Document Name *</label>
              <input required value={form.filename} onChange={e => setForm({...form, filename: e.target.value})}
                placeholder="e.g., Commercial Property Guidelines v2026"
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Inspection Type</label>
              <select value={form.inspection_type} onChange={e => setForm({...form, inspection_type: e.target.value})}
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 bg-white">
                {INSPECTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">
              Guidelines Content * <span className="text-slate-400 font-normal">(paste or type your underwriting guidelines)</span>
            </label>
            <textarea required value={form.content} onChange={e => setForm({...form, content: e.target.value})}
              rows={8} placeholder="Paste your underwriting guidelines, loss control standards, acceptable risk criteria, inspection checklists, etc."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 resize-none" />
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} data-testid="save-guideline-btn"
              className="flex items-center gap-2 px-5 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors disabled:opacity-60">
              <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Guidelines"}
            </button>
            <button type="button" onClick={() => setAdding(false)} className="px-5 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-8 text-center"><RefreshCw className="w-5 h-5 animate-spin text-navy mx-auto" /></div>
      ) : guidelines.length === 0 ? (
        <div className="py-12 text-center bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
          <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No guidelines uploaded yet</p>
          <p className="text-slate-400 text-sm mt-1">The AI will use standard industry guidelines until you add your own</p>
        </div>
      ) : (
        <div className="space-y-2">
          {guidelines.map(g => (
            <div key={g.id} className="flex items-center gap-4 bg-white border border-slate-200 rounded-lg p-4" data-testid={`guideline-${g.id}`}>
              <BookOpen className="w-5 h-5 text-navy flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-700 text-sm truncate">{g.filename}</div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {INSPECTION_TYPES.find(t => t.value === g.inspection_type)?.label || g.inspection_type} — {g.content.length} chars
                </div>
              </div>
              <button onClick={() => handleDelete(g.id)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors" data-testid={`delete-guideline-${g.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BrokerSettingsCard() {
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/broker/settings").then(r => setEnabled(r.data.brokers_can_view_links)).catch(() => {});
  }, []);

  const toggle = async () => {
    setSaving(true);
    try {
      const res = await api.put("/broker/settings", { brokers_can_view_links: !enabled });
      setEnabled(res.data.brokers_can_view_links);
    } catch {}
    setSaving(false);
  };

  return (
    <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-4 flex items-center justify-between" data-testid="broker-settings-card">
      <div>
        <p className="text-sm font-semibold text-slate-700">Broker survey link visibility</p>
        <p className="text-xs text-slate-500 mt-0.5">
          When ON, brokers can copy the insured's survey link. Default OFF preserves insured attestation integrity — brokers can still resend invitations.
        </p>
      </div>
      <button onClick={toggle} disabled={saving} data-testid="broker-links-toggle"
        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${enabled ? "bg-navy" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? "translate-x-5" : ""}`} />
      </button>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "underwriter" });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");

  const load = async () => {
    setLoading(true);
    try { const res = await api.get("/carrier/users"); setUsers(res.data); }
    catch {} setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/carrier/users", form);
      setAdding(false); setForm({ name: "", email: "", password: "", role: "underwriter" });
      setSuccess("User created successfully"); setTimeout(() => setSuccess(""), 3000);
      load();
    } catch {} setSaving(false);
  };

  const ROLE_COLORS = { admin: "bg-purple-100 text-purple-700", underwriter: "bg-blue-100 text-blue-700", loss_control: "bg-green-100 text-green-700", broker: "bg-amber-100 text-amber-700" };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-semibold text-slate-700">Team Members</h3>
          <p className="text-sm text-slate-500 mt-0.5">Manage users who can access the platform</p>
        </div>
        <button onClick={() => setAdding(true)} data-testid="add-user-btn"
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors">
          <Plus className="w-4 h-4" /> Add User
        </button>
      </div>

      {success && <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm"><CheckCircle className="w-4 h-4" />{success}</div>}

      <BrokerSettingsCard />

      {adding && (
        <form onSubmit={handleAdd} className="bg-navy/5 border border-navy/20 rounded-xl p-5 space-y-4 animate-slide-up">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Full Name *</label>
              <input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Jane Smith"
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Email *</label>
              <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="jane@company.com"
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Password *</label>
              <input required type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="••••••••"
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Role *</label>
              <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 bg-white">
                <option value="admin">Admin</option>
                <option value="underwriter">Underwriter</option>
                <option value="loss_control">Loss Control</option>
                <option value="broker">Broker</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} data-testid="save-user-btn"
              className="flex items-center gap-2 px-5 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors disabled:opacity-60">
              <Save className="w-4 h-4" /> {saving ? "Creating..." : "Create User"}
            </button>
            <button type="button" onClick={() => setAdding(false)} className="px-5 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
          </div>
        </form>
      )}

      {loading ? <div className="py-8 text-center"><RefreshCw className="w-5 h-5 animate-spin text-navy mx-auto" /></div> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map(u => (
                <tr key={u.id} data-testid={`user-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-navy/10 rounded-full flex items-center justify-center text-navy text-xs font-bold">{u.name?.[0]?.toUpperCase()}</div>
                      <span className="font-medium text-slate-700">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{u.email}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[u.role] || "bg-slate-100 text-slate-600"}`}>{u.role}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs ${u.active ? "text-green-600" : "text-red-500"}`}>{u.active ? "Active" : "Inactive"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ProfileTab() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const load = () => {
    api.get("/carrier/profile").then(r => {
      setProfile(r.data);
      setForm(r.data);
      if (r.data.logo_url) setLogoPreview(`${BACKEND_URL}${r.data.logo_url}`);
    }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.put("/carrier/profile", form);
      setSuccess(true); setTimeout(() => setSuccess(false), 3000);
    } catch {} setSaving(false);
  };

  const handleLogoUpload = async (file) => {
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const res = await api.post("/carrier/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoPreview(`${BACKEND_URL}${res.data.logo_url}`);
      setForm(prev => ({ ...prev, logo_url: res.data.logo_url }));
      setSuccess(true); setTimeout(() => setSuccess(false), 3000);
    } catch {} finally { setUploadingLogo(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) handleLogoUpload(file);
  };

  if (loading) return <div className="py-8 text-center"><RefreshCw className="w-5 h-5 animate-spin text-navy mx-auto" /></div>;

  return (
    <div className="space-y-6 max-w-lg">
      <div>
        <h3 className="font-heading font-semibold text-slate-700">Carrier Profile</h3>
        <p className="text-sm text-slate-500 mt-0.5">These details appear on all generated reports and the insured portal</p>
      </div>
      {success && <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm"><CheckCircle className="w-4 h-4" />Saved!</div>}

      {/* Logo Upload */}
      <div>
        <label className="block text-sm font-medium text-slate-600 mb-2">Carrier Logo</label>
        <div
          onDrop={handleDrop} onDragOver={e => e.preventDefault()}
          onClick={() => logoRef.current?.click()}
          data-testid="logo-upload-zone"
          className="border-2 border-dashed border-slate-200 rounded-xl p-5 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-navy/50 hover:bg-slate-50 transition-all min-h-[120px]">
          <input type="file" ref={logoRef} accept="image/*" className="hidden"
            onChange={e => { if (e.target.files[0]) handleLogoUpload(e.target.files[0]); }} />
          {uploadingLogo ? (
            <RefreshCw className="w-6 h-6 animate-spin text-navy" />
          ) : logoPreview ? (
            <div className="flex flex-col items-center gap-2">
              <img src={logoPreview} alt="Carrier logo" className="max-h-16 max-w-[200px] object-contain rounded" data-testid="logo-preview" />
              <span className="text-xs text-slate-400">Click or drag to replace</span>
            </div>
          ) : (
            <>
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-slate-400" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-slate-600">Drop your logo here</p>
                <p className="text-xs text-slate-400">PNG, JPG, or SVG · Appears on reports and insured portal</p>
              </div>
            </>
          )}
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        {[
          { label: "Company Name", key: "name", placeholder: "Acme Insurance Company" },
          { label: "Contact Email", key: "contact_email", placeholder: "losscontrol@company.com" },
          { label: "Report Header", key: "report_header", placeholder: "Loss Control Division" },
          { label: "Report Footer", key: "report_footer", placeholder: "Confidential — for named insured only" },
        ].map(field => (
          <div key={field.key}>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">{field.label}</label>
            <input value={form[field.key] || ""} onChange={e => setForm({...form, [field.key]: e.target.value})}
              placeholder={field.placeholder}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
          </div>
        ))}
        <button type="submit" disabled={saving} data-testid="save-profile-btn"
          className="flex items-center gap-2 px-6 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Profile"}
        </button>
      </form>
    </div>
  );
}

function TrainingTab() {
  const [precedents, setPrecedents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/carrier/precedents");
      setPrecedents(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (p) => {
    await api.patch(`/carrier/precedents/${p.id}`, { active: !p.active });
    setPrecedents(prev => prev.map(x => x.id === p.id ? { ...x, active: !p.active } : x));
  };

  const handleDelete = async (id) => {
    await api.delete(`/carrier/precedents/${id}`);
    setPrecedents(prev => prev.filter(p => p.id !== id));
  };

  const filtered = filter === "all" ? precedents : precedents.filter(p => p.inspection_type === filter);
  const activeCt = precedents.filter(p => p.active).length;

  // Summary by type
  const byType = Object.entries(
    precedents.reduce((acc, p) => {
      const t = p.inspection_type || "commercial_property";
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {})
  );

  return (
    <div className="space-y-5" data-testid="training-tab">
      {/* Header */}
      <div>
        <h3 className="font-heading font-semibold text-slate-700">AI Training Library</h3>
        <p className="text-sm text-slate-500 mt-0.5">
          Every finding your team approves trains the AI to think like your loss control program.
          {activeCt > 0 && <span className="ml-1 font-medium text-navy">{activeCt} active example{activeCt !== 1 ? "s" : ""} calibrating your AI.</span>}
        </p>
      </div>

      {/* Summary cards */}
      {byType.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {byType.map(([type, count]) => (
            <div key={type} className="bg-navy/5 border border-navy/10 rounded-lg px-4 py-3 flex items-center justify-between" data-testid={`type-count-${type}`}>
              <span className="text-sm font-medium text-slate-600">{TYPE_LABELS[type] || type}</span>
              <span className="text-lg font-bold text-navy font-heading">{count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      {precedents.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {[{ value: "all", label: "All" }, ...INSPECTION_TYPES.slice(1)].map(t => (
            <button key={t.value} onClick={() => setFilter(t.value)} data-testid={`filter-${t.value}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === t.value ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="py-8 text-center"><RefreshCw className="w-5 h-5 animate-spin text-navy mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
          <Brain className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No training examples yet</p>
          <p className="text-slate-400 text-sm mt-1 max-w-xs mx-auto">
            Approve findings in the Review interface to start building your AI training library.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => (
            <div key={p.id} data-testid={`precedent-${p.id}`}
              className={`border rounded-lg p-4 transition-opacity ${p.active ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200 opacity-60"}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${SEV_COLORS[p.category] || SEV_COLORS.Low}`}>{p.category}</span>
                    <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{TYPE_LABELS[p.inspection_type] || p.inspection_type}</span>
                    <span className="text-xs text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full">{SOURCE_LABELS[p.source] || p.source}</span>
                    {!p.active && <span className="text-xs text-slate-400 italic">Inactive</span>}
                  </div>
                  <p className="text-sm font-medium text-slate-800">{p.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{p.description}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => handleToggle(p)} title={p.active ? "Deactivate" : "Activate"}
                    data-testid={`toggle-precedent-${p.id}`}
                    className="p-1.5 text-slate-400 hover:text-navy transition-colors">
                    {p.active ? <ToggleRight className="w-5 h-5 text-navy" /> : <ToggleLeft className="w-5 h-5" />}
                  </button>
                  <button onClick={() => handleDelete(p.id)} title="Delete"
                    data-testid={`delete-precedent-${p.id}`}
                    className="p-1.5 text-slate-400 hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Q_TYPES = [
  { value: "text", label: "Short Text" },
  { value: "yes_no", label: "Yes / No / N/A" },
  { value: "select", label: "Multiple Choice" },
  { value: "number", label: "Number" },
  { value: "photo", label: "Photo Upload" },
];

const INSP_TYPES = [
  { key: "commercial_property", label: "Commercial Property" },
  { key: "workers_comp",        label: "Workers' Comp" },
  { key: "commercial_auto",     label: "Commercial Auto" },
  { key: "general_liability",   label: "General Liability" },
  { key: "habitational",        label: "Habitational" },
];

function InspectionFieldTemplatesSection() {
  const [selectedType, setSelectedType] = useState("commercial_property");
  const [allTemplates, setAllTemplates] = useState({});
  const [fields, setFields] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadRef] = useState(() => React.createRef());
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    api.get("/carrier/inspection-templates")
      .then(r => {
        setAllTemplates(r.data || {});
        const tmpl = r.data?.[selectedType];
        setFields(tmpl?.fields || []);
        setIsDirty(false);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const tmpl = allTemplates[selectedType];
    setFields(tmpl?.fields || []);
    setIsDirty(false);
  }, [selectedType, allTemplates]);

  const extractFromGuidelines = async () => {
    setExtracting(true);
    try {
      const res = await api.post("/carrier/inspection-templates/extract", { inspection_type: selectedType });
      setFields(res.data.fields);
      setIsDirty(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not extract fields. Make sure guidelines are uploaded in the AI Guidelines tab.");
    } finally { setExtracting(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("inspection_type", selectedType);
      const res = await api.post("/carrier/inspection-templates/extract-from-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setFields(res.data.fields);
      setIsDirty(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not parse file. Try a PDF or Word document.");
    } finally { setUploadingFile(false); e.target.value = ""; }
  };

  const updateField = (idx, key, val) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, [key]: val } : f));
    setIsDirty(true);
  };
  const removeField = (idx) => { setFields(prev => prev.filter((_, i) => i !== idx)); setIsDirty(true); };
  const addField = () => { setFields(prev => [...prev, { key: `field_${Date.now()}`, label: "New Field", required: false }]); setIsDirty(true); };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await api.put(`/carrier/inspection-templates/${selectedType}`, { fields, source: "manual" });
      setAllTemplates(prev => ({ ...prev, [selectedType]: { fields } }));
      setSaved(true); setIsDirty(false);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { alert("Save failed"); }
    finally { setSaving(false); }
  };

  const deleteTemplate = async () => {
    if (!window.confirm("Remove this custom template and revert to system defaults?")) return;
    await api.delete(`/carrier/inspection-templates/${selectedType}`);
    setAllTemplates(prev => { const n = { ...prev }; delete n[selectedType]; return n; });
    setFields([]); setIsDirty(false);
  };

  const hasTemplate = !!allTemplates[selectedType]?.fields?.length;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-6" data-testid="inspection-templates-section">
      <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/60">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-slate-700 text-sm">Inspection Field Templates</h3>
            <p className="text-xs text-slate-500 mt-0.5">Define the specific data fields your surveys must capture per line of business. These guide the AI agent's questions AND shape the internal underwriter report.</p>
          </div>
          <span className="text-xs bg-navy/10 text-navy px-2 py-0.5 rounded-full font-medium">Phase 2</span>
        </div>
      </div>

      {/* Type selector */}
      <div className="flex border-b border-slate-200 overflow-x-auto">
        {INSP_TYPES.map(t => (
          <button key={t.key} onClick={() => setSelectedType(t.key)}
            data-testid={`insp-type-tab-${t.key}`}
            className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${selectedType === t.key ? "border-navy text-navy bg-white" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}
            {allTemplates[t.key]?.fields?.length ? <span className="ml-1.5 bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full text-xs">{allTemplates[t.key].fields.length}</span> : null}
          </button>
        ))}
      </div>

      <div className="p-5">
        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={extractFromGuidelines} disabled={extracting}
            data-testid="extract-from-guidelines-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-navy text-white rounded-lg text-xs font-medium hover:bg-navy/90 transition-colors disabled:opacity-60">
            <Brain className="w-3.5 h-3.5" />
            {extracting ? "Extracting..." : "Extract from Guidelines"}
          </button>
          <label data-testid="upload-template-btn" className={`flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors cursor-pointer ${uploadingFile ? "opacity-60" : ""}`}>
            <Upload className="w-3.5 h-3.5" />
            {uploadingFile ? "Parsing..." : "Upload Existing Format"}
            <input type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={handleFileUpload} disabled={uploadingFile} />
          </label>
          <button onClick={addField}
            data-testid="add-field-btn"
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Add Field
          </button>
        </div>

        {/* Field list */}
        {fields.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
            <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 text-sm font-medium">No template for {INSP_TYPES.find(t=>t.key===selectedType)?.label}</p>
            <p className="text-slate-400 text-xs mt-1">AI will use system-default fields. Extract from your guidelines to customise.</p>
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-6">#</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Field Label</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider w-24">Required</th>
                  <th className="px-3 py-2.5 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {fields.map((f, idx) => (
                  <tr key={idx} className="hover:bg-slate-50" data-testid={`field-row-${idx}`}>
                    <td className="px-3 py-2 text-slate-400 text-xs">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <input value={f.label} onChange={e => updateField(idx, "label", e.target.value)}
                        className="w-full px-2 py-1 border border-transparent hover:border-slate-200 focus:border-navy focus:ring-1 focus:ring-navy/30 rounded text-sm text-slate-700 bg-transparent focus:bg-white outline-none transition-colors" />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => updateField(idx, "required", !f.required)}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${f.required ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                        {f.required ? "Required" : "Optional"}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => removeField(idx)} className="text-slate-300 hover:text-red-500 transition-colors">
                        <X className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Save / delete row */}
        {(fields.length > 0 || hasTemplate) && (
          <div className="flex items-center justify-between mt-4">
            {hasTemplate ? (
              <button onClick={deleteTemplate} className="text-xs text-red-500 hover:text-red-700 transition-colors">
                Remove custom template
              </button>
            ) : <span />}
            <div className="flex items-center gap-3">
              {saved && <span className="flex items-center gap-1 text-green-600 text-xs font-medium" data-testid="template-saved-indicator"><CheckCircle className="w-3.5 h-3.5" /> Saved</span>}
              <button onClick={saveTemplate} disabled={saving || !isDirty}
                data-testid="save-template-btn"
                className="flex items-center gap-1.5 px-4 py-2 bg-navy text-white rounded-lg text-xs font-medium hover:bg-navy/90 transition-colors disabled:opacity-50">
                <Save className="w-3.5 h-3.5" />
                {saving ? "Saving..." : "Save Template"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, 'new' or id = editor
  const [form, setForm] = useState({ name: "", description: "", inspection_type: "commercial_property", questions: [] });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get("/carrier/templates"); setTemplates(res.data); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setForm({ name: "", description: "", inspection_type: "commercial_property", questions: [] });
    setEditing("new");
  };

  const startEdit = (t) => {
    setForm({ ...t });
    setEditing(t.id);
  };

  const addQuestion = () => {
    const q = { id: `q_${Date.now()}`, text: "", type: "yes_no", required: true, hint: "", options: [], placeholder: "" };
    setForm(prev => ({ ...prev, questions: [...prev.questions, q] }));
  };

  const updateQuestion = (idx, field, value) => {
    setForm(prev => {
      const qs = [...prev.questions];
      qs[idx] = { ...qs[idx], [field]: value };
      return { ...prev, questions: qs };
    });
  };

  const removeQuestion = (idx) => {
    setForm(prev => ({ ...prev, questions: prev.questions.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        const res = await api.post("/carrier/templates", form);
        setTemplates(prev => [res.data, ...prev]);
      } else {
        const res = await api.put(`/carrier/templates/${editing}`, form);
        setTemplates(prev => prev.map(t => t.id === editing ? res.data : t));
      }
      setEditing(null);
    } catch {} finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    await api.delete(`/carrier/templates/${id}`);
    setTemplates(prev => prev.filter(t => t.id !== id));
  };

  if (editing !== null) {
    return (
      <div className="space-y-5" data-testid="template-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-slate-700">{editing === "new" ? "New Template" : "Edit Template"}</h3>
          <button onClick={() => setEditing(null)} className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Template Name</label>
            <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))}
              placeholder="Commercial Property Site Visit"
              data-testid="template-name-input"
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Survey Type</label>
            <select value={form.inspection_type} onChange={e => setForm(p => ({...p, inspection_type: e.target.value}))}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 bg-white">
              {INSPECTION_TYPES.slice(1).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Description</label>
          <input value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))}
            placeholder="Brief description shown to the insured"
            className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Questions ({form.questions.length})</label>
            <button onClick={addQuestion} data-testid="add-question-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-navy/10 text-navy text-xs font-medium rounded-lg hover:bg-navy/20 transition-colors">
              <Plus className="w-3.5 h-3.5" /> Add Question
            </button>
          </div>
          {form.questions.length === 0 && (
            <div className="py-8 text-center bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 text-slate-400 text-sm">
              No questions yet — click "Add Question" to start building
            </div>
          )}
          <div className="space-y-3">
            {form.questions.map((q, idx) => (
              <div key={q.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3" data-testid={`question-editor-${idx}`}>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-navy text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                  <input value={q.text} onChange={e => updateQuestion(idx, "text", e.target.value)}
                    placeholder="Question text..."
                    className="flex-1 h-9 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 bg-white" />
                  <select value={q.type} onChange={e => updateQuestion(idx, "type", e.target.value)}
                    className="h-9 px-2 border border-slate-200 rounded-lg text-xs focus:outline-none bg-white">
                    {Q_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <button onClick={() => removeQuestion(idx)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {q.type === "select" && (
                  <input value={q.options?.join(", ") || ""} onChange={e => updateQuestion(idx, "options", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                    placeholder="Option A, Option B, Option C"
                    className="w-full h-9 px-3 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-navy/30 bg-white" />
                )}
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                    <input type="checkbox" checked={q.required || false} onChange={e => updateQuestion(idx, "required", e.target.checked)} className="w-3 h-3 accent-navy" />
                    Required
                  </label>
                  <input value={q.hint || ""} onChange={e => updateQuestion(idx, "hint", e.target.value)}
                    placeholder="Optional hint text for insured..."
                    className="flex-1 h-7 px-2 border border-slate-200 rounded text-xs focus:outline-none bg-white" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button onClick={handleSave} disabled={saving || !form.name || form.questions.length === 0} data-testid="save-template-btn"
            className="flex items-center gap-2 px-5 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Template"}
          </button>
          <button onClick={() => setEditing(null)} className="px-5 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="templates-tab">
      <InspectionFieldTemplatesSection />
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-semibold text-slate-700">Survey Templates</h3>
          <p className="text-sm text-slate-500 mt-0.5">Build structured questionnaires for Mode 2 surveys — no AI chat required</p>
        </div>
        <button onClick={startNew} data-testid="new-template-btn"
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors">
          <Plus className="w-4 h-4" /> New Template
        </button>
      </div>
      {loading ? (
        <div className="py-8 text-center"><RefreshCw className="w-5 h-5 animate-spin text-navy mx-auto" /></div>
      ) : templates.length === 0 ? (
        <div className="py-12 text-center bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No templates yet</p>
          <p className="text-slate-400 text-sm mt-1 max-w-xs mx-auto">Create your first questionnaire to offer Mode 2 surveys to insureds</p>
          <button onClick={startNew} className="mt-4 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors">
            Create Template
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map(t => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4" data-testid={`template-${t.id}`}>
              <div className="w-9 h-9 bg-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-navy" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">{t.name}</p>
                <p className="text-xs text-slate-400">{TYPE_LABELS[t.inspection_type] || t.inspection_type} · {t.questions?.length || 0} questions</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => startEdit(t)} data-testid={`edit-template-${t.id}`}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-navy hover:text-navy transition-colors">Edit</button>
                <button onClick={() => handleDelete(t.id)} data-testid={`delete-template-${t.id}`}
                  className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ComplianceTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    api.get("/analytics/compliance")
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-12 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-navy" /></div>;
  if (!data) return null;

  const copyLink = (token, mode) => {
    const url = `${window.location.origin}/${mode === "form" ? "survey" : "inspection"}/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedId(token);
    setTimeout(() => setCopiedId(null), 2500);
  };

  const TYPE_LABELS_C = {
    commercial_property: "Comm. Property",
    workers_comp: "Workers' Comp",
    commercial_auto: "Comm. Auto",
    general_liability: "Gen. Liability",
    habitational: "Habitational",
  };

  const FUNNEL = [
    { label: "Surveys Sent", value: data.funnel?.sent || data.total, color: "bg-slate-400", pct: 100 },
    { label: "In Progress",  value: data.funnel?.in_progress || 0, color: "bg-blue-500",   pct: data.total > 0 ? Math.round((data.funnel?.in_progress || 0) / data.total * 100) : 0 },
    { label: "Completed",    value: data.funnel?.completed || 0,   color: "bg-navy",        pct: data.total > 0 ? Math.round((data.funnel?.completed || 0) / data.total * 100) : 0 },
    { label: "Finalized",    value: data.funnel?.finalized || 0,   color: "bg-green-600",   pct: data.total > 0 ? Math.round((data.funnel?.finalized || 0) / data.total * 100) : 0 },
  ];

  const maxTrend = Math.max(...data.weekly_trend.map(w => w.total), 1);

  return (
    <div className="space-y-6" data-testid="compliance-tab">
      <div>
        <h3 className="font-heading font-semibold text-slate-700">Survey Compliance Dashboard</h3>
        <p className="text-sm text-slate-500 mt-0.5">Track insured survey completion rates, pipeline health, and follow-up needs</p>
      </div>

      {/* Hero metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Completion Rate",     value: `${data.completion_rate}%`,
            sub: `${data.completed} of ${data.total} surveys`,
            color: data.completion_rate >= 70 ? "text-green-600" : "text-amber-600", Icon: CheckCircle },
          { label: "Avg Days to Complete", value: data.avg_days_to_complete > 0 ? `${data.avg_days_to_complete}d` : "—",
            sub: "From sent to submitted", color: "text-navy", Icon: TrendingUp },
          { label: "Active Surveys",       value: data.in_progress + data.pending,
            sub: `${data.pending} pending · ${data.in_progress} in progress`, color: "text-blue-600", Icon: BarChart2 },
          { label: "Overdue",              value: data.overdue_count,
            sub: data.overdue_count > 0 ? "Need follow-up" : "All on track",
            color: data.overdue_count > 0 ? "text-red-600" : "text-green-600", Icon: AlertCircle },
        ].map(card => (
          <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-5"
            data-testid={`compliance-metric-${card.label.toLowerCase().replace(/\s+/g,"-")}`}>
            <div className="flex items-center gap-1.5 mb-2">
              <card.Icon className={`w-3.5 h-3.5 ${card.color}`} />
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{card.label}</span>
            </div>
            <div className={`font-heading text-3xl font-bold ${card.color}`}>{card.value}</div>
            <p className="text-xs text-slate-400 mt-1">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Pipeline + By Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Survey Pipeline</h4>
          {FUNNEL.map(step => (
            <div key={step.label} data-testid={`funnel-step-${step.label.toLowerCase().replace(/\s/g,"-")}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-slate-600">{step.label}</span>
                <span className="text-sm font-semibold text-slate-800">{step.value}
                  <span className="text-slate-400 font-normal text-xs ml-1">({step.pct}%)</span>
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5">
                <div className={`${step.color} h-2.5 rounded-full transition-all duration-700`}
                  style={{ width: `${Math.max(step.pct, step.value > 0 ? 2 : 0)}%` }} />
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Completion by Type</h4>
          {data.by_type.length === 0 ? (
            <p className="text-slate-400 text-sm py-4 text-center">No survey data yet</p>
          ) : data.by_type.map(t => (
            <div key={t.type} data-testid={`type-rate-${t.type}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm text-slate-600">{TYPE_LABELS_C[t.type] || t.type}</span>
                <span className="text-sm font-semibold text-slate-800">{t.completed}/{t.total}
                  <span className="text-slate-400 font-normal text-xs ml-1">({t.rate}%)</span>
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2.5">
                <div className="bg-navy h-2.5 rounded-full transition-all duration-700"
                  style={{ width: `${Math.max(t.rate, t.completed > 0 ? 2 : 0)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly trend sparkline */}
      {data.weekly_trend.some(w => w.total > 0) && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Survey Volume — Last 8 Weeks</h4>
          <div className="flex items-end gap-2 h-16">
            {data.weekly_trend.map((w, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative" data-testid={`trend-week-${i}`}>
                <div className="absolute bottom-full mb-1 hidden group-hover:flex flex-col items-center z-10">
                  <div className="bg-slate-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                    {w.week}: {w.completed}/{w.total} ({w.rate}%)
                  </div>
                </div>
                <div className="w-full bg-slate-100 rounded-t relative" style={{ height: "100%" }}>
                  <div className="w-full bg-navy/20 rounded-t absolute bottom-0"
                    style={{ height: `${maxTrend > 0 ? Math.max(w.total / maxTrend * 100, w.total > 0 ? 8 : 0) : 0}%` }} />
                  <div className="w-full bg-navy rounded-t absolute bottom-0 transition-all duration-500"
                    style={{ height: `${maxTrend > 0 ? Math.max(w.completed / maxTrend * 100, w.completed > 0 ? 4 : 0) : 0}%` }} />
                </div>
                <span className="text-xs text-slate-400 truncate w-full text-center">{w.week.split(" ")[1]}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-2">
            <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-3 h-2 bg-navy rounded-sm inline-block" /> Completed</span>
            <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-3 h-2 bg-navy/20 rounded-sm inline-block" /> Sent</span>
          </div>
        </div>
      )}

      {/* Escalation queue */}
      {data.overdue.length > 0 ? (
        <div className="bg-white border border-red-200 rounded-xl overflow-hidden" data-testid="escalation-queue">
          <div className="px-5 py-4 border-b border-red-100 bg-red-50/60 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <h4 className="text-sm font-semibold text-red-700">
              Escalation Queue — {data.overdue.length} Survey{data.overdue.length !== 1 ? "s" : ""} Need Follow-Up
            </h4>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Insured</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Overdue</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Reminders</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.overdue.map(item => (
                <tr key={item.id} data-testid={`overdue-row-${item.id}`} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-700">{item.insured_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs hidden md:table-cell">{TYPE_LABELS_C[item.inspection_type] || item.inspection_type}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${item.status === "in_progress" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                      {item.status === "in_progress" ? "In Progress" : "Pending"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-red-600 font-bold text-sm">{item.days_overdue}d overdue</span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 hidden sm:table-cell">{item.reminders_sent} sent</td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`escalation-copy-${item.id}`}
                      onClick={() => copyLink(item.access_token, item.mode)}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-100 text-slate-600 hover:bg-navy hover:text-white rounded-md text-xs font-medium transition-colors"
                    >
                      {copiedId === item.access_token
                        ? <><CheckCircle className="w-3 h-3" /> Copied!</>
                        : <><Copy className="w-3 h-3" /> Resend Link</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : data.total > 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex items-center gap-3" data-testid="no-overdue-banner">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-700">No overdue surveys</p>
            <p className="text-xs text-green-600 mt-0.5">All active surveys are within their due dates — great compliance rate!</p>
          </div>
        </div>
      ) : (
        <div className="py-10 text-center bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
          <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No surveys ordered yet</p>
          <p className="text-slate-400 text-sm mt-1">Compliance data will appear here as you order surveys.</p>
        </div>
      )}
    </div>
  );
}

const ACTION_COLORS = {  login: "text-blue-600 bg-blue-50",
  survey_created: "text-green-600 bg-green-50",
  survey_submitted: "text-purple-600 bg-purple-50",
  finding_approved: "text-emerald-600 bg-emerald-50",
  finding_dismissed: "text-slate-500 bg-slate-100",
  report_generated: "text-orange-600 bg-orange-50",
  carrier_registered: "text-navy bg-blue-50",
};

function ActivityTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/carrier/audit-logs").then(r => setLogs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4" data-testid="activity-tab">
      <div>
        <h3 className="font-heading font-semibold text-slate-700">Activity Log</h3>
        <p className="text-sm text-slate-500 mt-0.5">All actions taken by your team — last 100 events</p>
      </div>
      {loading ? (
        <div className="py-8 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-navy" /></div>
      ) : logs.length === 0 ? (
        <div className="py-10 text-center bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 text-slate-400 text-sm">
          No activity recorded yet
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Details</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Time</th>
            </tr></thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={log.id} className={`border-b border-slate-100 ${i % 2 === 0 ? "" : "bg-slate-50/50"}`} data-testid={`log-row-${i}`}>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ACTION_COLORS[log.action] || "text-slate-600 bg-slate-100"}`}>{log.action}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{log.user_name || log.user_email || "—"}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs max-w-[200px] truncate">{log.details || "—"}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{log.timestamp ? new Date(log.timestamp).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SecurityTab() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  // MFA state
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [reenrollMode, setReenrollMode] = useState(false);
  const [qrB64, setQrB64] = useState("");
  const [mfaSecret, setMfaSecret] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaSaving, setMfaSaving] = useState(false);
  const [mfaSuccess, setMfaSuccess] = useState("");
  const [mfaError, setMfaError] = useState("");
  const [secretCopied, setSecretCopied] = useState(false);

  // Data management state
  const [exporting, setExporting] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  useEffect(() => {
    api.get("/auth/mfa/status")
      .then(r => setMfaStatus(r.data))
      .catch(console.error)
      .finally(() => setMfaLoading(false));
  }, []);

  const startReenroll = async () => {
    setMfaError("");
    setMfaSaving(true);
    try {
      const r = await api.post("/auth/mfa/re-enroll");
      setQrB64(r.data.qr_image_base64);
      setMfaSecret(r.data.secret);
      setReenrollMode(true);
    } catch (e) { setMfaError("Failed to start re-enrollment."); }
    finally { setMfaSaving(false); }
  };

  const confirmReenroll = async (e) => {
    e.preventDefault();
    setMfaError("");
    setMfaSaving(true);
    try {
      await api.post("/auth/mfa/confirm-reenroll", { code: mfaCode });
      setMfaSuccess("MFA updated successfully. Your new authenticator key is active.");
      setReenrollMode(false);
      setMfaCode("");
      setMfaStatus({ ...mfaStatus, mfa_enabled: true, mfa_enrolled_at: new Date().toISOString() });
    } catch (e) { setMfaError(e.response?.data?.detail || "Invalid code."); }
    finally { setMfaSaving(false); }
  };

  const copySecret = () => { navigator.clipboard.writeText(mfaSecret); setSecretCopied(true); setTimeout(() => setSecretCopied(false), 2000); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await api.get("/carrier/export-data", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `RiskSurvey_DataExport_${new Date().toISOString().slice(0,10)}.json`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { alert("Export failed. Please try again."); }
    finally { setExporting(false); }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== "DELETE") return;
    setDeleting(true);
    try {
      const r = await api.delete("/carrier/data", { data: { confirm: "DELETE" } });
      setDeleteResult(r.data);
      setDeleteMode(false);
      setDeleteConfirm("");
    } catch (e) { alert("Deletion failed: " + (e.response?.data?.detail || e.message)); }
    finally { setDeleting(false); }
  };

  if (mfaLoading) return <div className="py-10 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-navy" /></div>;

  return (
    <div className="space-y-8" data-testid="security-tab">
      {/* ── MFA Section ── */}
      <div className="space-y-5">
        <div className="border-b border-slate-100 pb-4">
          <h3 className="font-heading font-semibold text-slate-800">Multi-Factor Authentication (MFA)</h3>
          <p className="text-xs text-slate-400 mt-0.5">TOTP-based MFA is required for all carrier admins</p>
        </div>

        {mfaError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0" />{mfaError}</div>}
        {mfaSuccess && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4 flex-shrink-0" />{mfaSuccess}</div>}

        {!reenrollMode ? (
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200" data-testid="mfa-status-card">
            <div>
              <p className="font-medium text-sm text-slate-700">Authenticator App</p>
              {mfaStatus?.mfa_enabled ? (
                <>
                  <p className="text-xs text-green-600 font-medium mt-0.5">Active &mdash; enrolled {mfaStatus.mfa_enrolled_at ? new Date(mfaStatus.mfa_enrolled_at).toLocaleDateString() : ""}</p>
                </>
              ) : (
                <p className="text-xs text-amber-600 font-medium mt-0.5">Not enrolled &mdash; you'll be prompted at next login</p>
              )}
            </div>
            <button
              data-testid="reenroll-mfa-btn"
              onClick={startReenroll}
              disabled={mfaSaving}
              className="text-sm px-4 py-2 border border-navy text-navy rounded-lg font-medium hover:bg-navy hover:text-white transition-colors disabled:opacity-60"
            >
              {mfaSaving ? "Loading..." : mfaStatus?.mfa_enabled ? "Re-enroll" : "Set Up Now"}
            </button>
          </div>
        ) : (
          <div className="space-y-4 p-5 bg-slate-50 rounded-xl border border-slate-200" data-testid="reenroll-form">
            <p className="text-sm font-medium text-slate-700">Scan this QR code with Google Authenticator or Authy, then enter the 6-digit code to confirm.</p>
            {qrB64 && <div className="flex justify-center"><img src={`data:image/png;base64,${qrB64}`} alt="MFA QR" className="w-44 h-44 rounded-lg border border-slate-200" /></div>}
            <div className="p-3 bg-white rounded-lg border border-slate-200">
              <p className="text-xs text-slate-500 mb-1">Manual entry key:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono text-slate-700 break-all">{mfaSecret}</code>
                <button type="button" onClick={copySecret} className="text-navy hover:text-navy/70">
                  {secretCopied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <form onSubmit={confirmReenroll} className="flex gap-3">
              <input data-testid="reenroll-code-input" type="text" inputMode="numeric" maxLength={6} value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/\D/g,""))} placeholder="000000"
                className="flex-1 h-10 px-3 text-center font-mono tracking-widest border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
              <button type="submit" disabled={mfaSaving || mfaCode.length < 6} data-testid="reenroll-confirm-btn"
                className="px-5 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 transition-colors disabled:opacity-60">
                {mfaSaving ? "Confirming..." : "Confirm"}
              </button>
              <button type="button" onClick={() => { setReenrollMode(false); setMfaCode(""); }}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-500 hover:bg-slate-100 transition-colors">
                Cancel
              </button>
            </form>
          </div>
        )}
      </div>

      {/* ── Data Management ── */}
      <div className="space-y-5 border-t border-slate-100 pt-6">
        <div className="border-b border-slate-100 pb-4">
          <h3 className="font-heading font-semibold text-slate-800">Data Management</h3>
          <p className="text-xs text-slate-400 mt-0.5">Export or delete your carrier data (SOC2 data portability &amp; right to erasure)</p>
        </div>

        {deleteResult && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
            <p className="font-semibold mb-1">Data deleted successfully</p>
            <p>{deleteResult.inspections} inspections, {deleteResult.findings} findings, {deleteResult.reports} reports, {deleteResult.job_orders} job orders removed.</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Export */}
          <div className="p-5 border border-slate-200 rounded-xl">
            <h4 className="font-medium text-sm text-slate-700 mb-1">Export All Data</h4>
            <p className="text-xs text-slate-400 mb-4">Download a JSON file of all your inspections, findings, reports, and users (passwords excluded).</p>
            <button data-testid="export-data-btn" onClick={handleExport} disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 transition-colors disabled:opacity-60">
              <FileText className="w-4 h-4" />{exporting ? "Exporting..." : "Export Data"}
            </button>
          </div>
          {/* Delete */}
          <div className="p-5 border border-red-100 rounded-xl bg-red-50/30">
            <h4 className="font-medium text-sm text-red-700 mb-1">Delete Insured Data</h4>
            <p className="text-xs text-slate-400 mb-4">Permanently delete all inspections, findings, and reports. Your carrier account and users are kept.</p>
            {!deleteMode ? (
              <button data-testid="delete-data-btn" onClick={() => setDeleteMode(true)}
                className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors">
                <Trash2 className="w-4 h-4" />Delete Data
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-xs font-medium text-red-700">Type <code className="bg-red-100 px-1 rounded">DELETE</code> to confirm:</p>
                <input data-testid="delete-confirm-input" type="text" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)}
                  placeholder="DELETE" className="w-full h-9 px-3 border border-red-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                <div className="flex gap-2">
                  <button data-testid="delete-confirm-btn" onClick={handleDelete} disabled={deleteConfirm !== "DELETE" || deleting}
                    className="px-4 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-60">
                    {deleting ? "Deleting..." : "Confirm Delete"}
                  </button>
                  <button onClick={() => { setDeleteMode(false); setDeleteConfirm(""); }} className="px-4 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-500 hover:bg-slate-100">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}




function LCTeamTab() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "lc_staff" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dispatchSettings, setDispatchSettings] = useState({ mode: "manual", threshold: 65, enabled: false });
  const [savingDispatch, setSavingDispatch] = useState(false);
  const [dispatchSaved, setDispatchSaved] = useState(false);

  const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

  const loadTeam = async () => {
    setLoading(true);
    try {
      const [teamRes, dispatchRes] = await Promise.all([
        api.get("/lc/team"),
        api.get("/lc/dispatch-settings"),
      ]);
      setTeam(teamRes.data);
      setDispatchSettings(dispatchRes.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadTeam(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await api.post("/lc/users", form);
      setShowCreate(false);
      setForm({ name: "", email: "", password: "", role: "lc_staff" });
      loadTeam();
    } catch (err) { setError(err.response?.data?.detail || "Failed to create user"); }
    setSaving(false);
  };

  const handleToggle = async (member) => {
    await api.put(`/lc/users/${member.id}`, { auto_assign_enabled: !member.auto_assign_enabled });
    loadTeam();
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Deactivate this team member?")) return;
    await api.delete(`/lc/users/${id}`);
    loadTeam();
  };

  const handleSaveDispatch = async () => {
    setSavingDispatch(true);
    try {
      await api.put("/lc/dispatch-settings", dispatchSettings);
      setDispatchSaved(true);
      setTimeout(() => setDispatchSaved(false), 2000);
    } catch {}
    setSavingDispatch(false);
  };

  const DISPATCH_MODES = [
    { key: "manual", label: "Manual", desc: "Manager manually assigns all inspections", icon: Users },
    { key: "auto", label: "Auto", desc: "Low-risk surveys auto-dispatch to territory rep", icon: Zap },
    { key: "hybrid", label: "Hybrid", desc: "Auto for routine, manual for high-risk decisions", icon: GitBranch },
  ];

  if (loading) return <div className="py-12 text-center text-slate-400"><RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" /></div>;

  return (
    <div className="space-y-8" data-testid="lc-team-tab">
      {/* ── Dispatch Mode (Admin configures) ── */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-slate-800 text-sm">Dispatch Mode</h3>
            <p className="text-xs text-slate-500 mt-0.5">Configure how completed surveys are routed to field reps</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-5">
          {DISPATCH_MODES.map(opt => {
            const isActive = dispatchSettings.mode === opt.key;
            return (
              <button key={opt.key} data-testid={`admin-dispatch-mode-${opt.key}`}
                onClick={() => setDispatchSettings(p => ({ ...p, mode: opt.key }))}
                className={`p-3 rounded-lg border-2 text-left transition-all ${isActive ? "border-navy bg-navy/5 text-navy" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                <opt.icon className="w-4 h-4 mb-1.5" />
                <p className="font-semibold text-xs">{opt.label}</p>
                <p className="text-xs opacity-70 mt-0.5 leading-relaxed">{opt.desc}</p>
              </button>
            );
          })}
        </div>

        {dispatchSettings.mode !== "manual" && (
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Risk Threshold: <span className="text-navy font-bold text-sm normal-case" data-testid="admin-threshold-value">{dispatchSettings.threshold}</span>
              </label>
            </div>
            <div className="relative h-4 rounded-full overflow-hidden bg-slate-200">
              <div className="absolute left-0 top-0 h-full bg-green-400 transition-all" style={{ width: `${dispatchSettings.threshold}%` }} />
              <div className="absolute top-0 h-full bg-amber-400 transition-all" style={{ left: `${dispatchSettings.threshold}%`, right: 0 }} />
            </div>
            <input type="range" min={0} max={100} step={5} value={dispatchSettings.threshold}
              onChange={e => setDispatchSettings(p => ({ ...p, threshold: Number(e.target.value) }))}
              className="w-full accent-navy" data-testid="admin-threshold-slider" />
            <div className="flex justify-between text-xs text-slate-400">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400"></span>Auto-dispatch (≤{dispatchSettings.threshold})</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400"></span>Manual decision (&gt;{dispatchSettings.threshold})</span>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1">
            <span className="text-xs text-slate-500">LC Module Enabled:</span>
            <button onClick={() => setDispatchSettings(p => ({ ...p, enabled: !p.enabled }))}
              data-testid="admin-toggle-lc-enabled"
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${dispatchSettings.enabled ? "bg-navy" : "bg-slate-300"}`}>
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${dispatchSettings.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>
          <button onClick={handleSaveDispatch} disabled={savingDispatch}
            className="flex items-center gap-2 bg-navy text-white px-4 py-2 rounded-lg text-xs font-medium hover:bg-navy/90 disabled:opacity-60"
            data-testid="admin-save-dispatch-btn">
            {savingDispatch ? <RefreshCw className="w-3 h-3 animate-spin" /> : dispatchSaved ? <CheckCircle className="w-3 h-3" /> : <Save className="w-3 h-3" />}
            {dispatchSaved ? "Saved!" : "Save Mode"}
          </button>
        </div>
      </div>

      {/* ── Team Management ── */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-semibold text-slate-800">Loss Control Team</h3>
          <p className="text-sm text-slate-500 mt-0.5">Manage field reps and managers for the LC module</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-navy text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-navy/90 transition-colors"
          data-testid="admin-add-lc-user-btn">
          <UserPlus className="w-4 h-4" /> Add Member
        </button>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
          <h4 className="font-semibold text-slate-700 text-sm">New Team Member</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Full Name</label>
              <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} required placeholder="Jane Smith"
                className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 bg-white"
                data-testid="admin-new-lc-name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Email</label>
              <input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} required placeholder="jane@company.com"
                className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 bg-white"
                data-testid="admin-new-lc-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Password</label>
              <input type="password" value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} required minLength={8} placeholder="Min. 8 chars"
                className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 bg-white" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Role</label>
              <select value={form.role} onChange={e => setForm(p => ({...p, role: e.target.value}))}
                className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm bg-white" data-testid="admin-new-lc-role">
                <option value="lc_staff">Field Staff</option>
                <option value="lc_manager">Manager</option>
              </select>
            </div>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 disabled:opacity-60"
              data-testid="admin-create-lc-submit">
              {saving ? "Creating..." : "Create Member"}
            </button>
          </div>
        </form>
      )}

      {team.length === 0 ? (
        <div className="py-12 text-center">
          <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No LC team members yet</p>
          <p className="text-slate-400 text-sm mt-1">Click "Add Member" to create your first loss control user</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Territories</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Auto-Assign</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {team.map(member => (
                <tr key={member.id} className={member.active === false ? "opacity-50" : ""} data-testid={`lc-member-row-${member.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{member.name}</div>
                    <div className="text-xs text-slate-400">{member.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${member.role === "lc_manager" ? "bg-navy/10 text-navy" : "bg-emerald-50 text-emerald-700"}`}>
                      {member.role === "lc_manager" ? "Manager" : "Field Staff"}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {member.territories?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {member.territories.map(t => (
                          <span key={t.state} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-semibold">
                            <MapPin className="w-2.5 h-2.5" />{t.state}
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-slate-400 text-xs italic">None</span>}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleToggle(member)} className="flex items-center gap-1 text-xs"
                      data-testid={`toggle-auto-assign-${member.id}`}>
                      {member.auto_assign_enabled !== false
                        ? <><ToggleRight className="w-5 h-5 text-navy" /><span className="text-green-600 font-medium">ON</span></>
                        : <><ToggleLeft className="w-5 h-5 text-slate-400" /><span className="text-slate-400">OFF</span></>}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${member.active === false ? "text-red-500" : "text-green-600"}`}>
                      {member.active === false ? "Inactive" : "Active"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {member.active !== false && (
                      <button onClick={() => handleDeactivate(member.id)} className="text-red-400 hover:text-red-600"
                        data-testid={`deactivate-lc-${member.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


export default function AdminDashboard() {
  const [tab, setTab] = useState("compliance");
  const TABS = [
    { key: "compliance", label: "Compliance",        icon: ClipboardList },
    { key: "guidelines", label: "AI Guidelines",     icon: BookOpen },
    { key: "training",   label: "AI Training",       icon: Brain },
    { key: "templates",  label: "Survey Templates",  icon: FileText },
    { key: "profiles",   label: "Business Profiles", icon: Briefcase },
    { key: "reclib",     label: "Rec Library",       icon: BookOpen },
    { key: "users",      label: "Team Members",      icon: Users },
    { key: "lc-team",    label: "Loss Control Team", icon: MapPin },
    { key: "usage",      label: "Usage & Plan",      icon: BarChart2 },
    { key: "email",      label: "Email Settings",    icon: Mail },
    { key: "api",        label: "API & Webhooks",    icon: Zap },
    { key: "security",   label: "Security",          icon: Shield },
    { key: "profile",    label: "Carrier Profile",   icon: Settings },
    { key: "activity",   label: "Activity Log",      icon: AlertCircle },
  ];

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-800">Admin Portal</h1>
          <p className="text-slate-500 text-sm mt-0.5">Configure your carrier's AI survey settings</p>
        </div>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg flex-wrap">
          {TABS.map(t => (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${tab === t.key ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          {tab === "compliance" && <ComplianceTab />}
          {tab === "guidelines" && <GuidelinesTab />}
          {tab === "training"   && <TrainingTab />}
          {tab === "templates"  && <TemplatesTab />}
          {tab === "profiles"   && <BusinessProfilesTab />}
          {tab === "reclib"     && <RecLibraryTab />}
          {tab === "users"      && <UsersTab />}
          {tab === "lc-team"    && <LCTeamTab />}
          {tab === "usage"      && <UsageTab />}
          {tab === "email"      && <EmailSettingsTab />}
          {tab === "api"        && <ApiWebhooksTab />}
          {tab === "security"   && <SecurityTab />}
          {tab === "profile"    && <ProfileTab />}
          {tab === "activity"   && <ActivityTab />}
        </div>
      </div>
    </Layout>
  );
}

function UsageTab() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/carrier/usage")
      .then(r => setUsage(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-10 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-navy" /></div>;

  if (!usage || !usage.plan) {
    return (
      <div className="py-10 text-center" data-testid="usage-no-plan">
        <BarChart2 className="w-8 h-8 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-medium">No plan assigned yet</p>
        <p className="text-slate-400 text-sm mt-1">Contact your RiskSurvey.ai account manager to get set up.</p>
      </div>
    );
  }

  const included = usage.included_surveys || 0;
  const completed = usage.surveys_completed_this_month || 0;
  const pct = included > 0 ? Math.min(100, Math.round((completed / included) * 100)) : 0;
  const isOver = completed > included;
  const barColor = isOver ? "bg-amber-500" : pct >= 80 ? "bg-amber-400" : "bg-green-500";

  return (
    <div className="space-y-6" data-testid="usage-tab">
      {/* Plan badge + month */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-lg text-slate-800">{usage.month} — Usage</h2>
          <p className="text-xs text-slate-400 mt-0.5">Surveys billed on completion</p>
        </div>
        <span className="px-3 py-1 rounded-full text-xs font-bold border bg-navy/5 text-navy border-navy/20">
          {usage.plan_name} Plan
        </span>
      </div>

      {/* Usage meter */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6" data-testid="usage-meter">
        <div className="flex items-end justify-between mb-3">
          <div>
            <span className="font-heading text-4xl font-bold text-slate-800">{completed}</span>
            <span className="text-slate-400 text-lg ml-1">/ {included} surveys</span>
          </div>
          <span className={`text-sm font-semibold ${isOver ? "text-amber-600" : "text-slate-500"}`}>
            {isOver ? `${usage.overage_count} over included` : `${included - completed} remaining`}
          </span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-3">
          <div className={`${barColor} h-3 rounded-full transition-all duration-500`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
        {isOver && (
          <p className="text-amber-600 text-xs font-medium mt-2">
            You've used {usage.overage_count} additional survey{usage.overage_count !== 1 ? "s" : ""} this month at ${usage.overage_rate}/each.
            Your account manager will follow up at month end.
          </p>
        )}
      </div>

      {/* Plan details */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Monthly Fee",      value: `$${usage.monthly_fee?.toLocaleString()}`,    icon: DollarSign,  color: "text-navy" },
          { label: "Included Surveys", value: usage.included_surveys,                        icon: BarChart2,   color: "text-green-600" },
          { label: "Overage Rate",     value: `$${usage.overage_rate}/survey`,               icon: TrendingUp,  color: "text-amber-600" },
          { label: "Est. Invoice",     value: `$${usage.estimated_invoice?.toLocaleString()}`, icon: CheckCircle, color: "text-slate-700" },
        ].map(card => (
          <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <card.icon className={`w-4 h-4 ${card.color}`} />
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{card.label}</span>
            </div>
            <div className="font-heading text-xl font-bold text-slate-800">{card.value}</div>
          </div>
        ))}
      </div>

      {/* Last month comparison */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
        <TrendingUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <p className="text-sm text-slate-500">
          Last month you completed <strong className="text-slate-700">{usage.surveys_completed_last_month}</strong> surveys.
          {completed > usage.surveys_completed_last_month
            ? ` You're on pace to exceed that this month.`
            : ` You're tracking at or below last month's volume.`}
        </p>
      </div>
    </div>
  );
}


function EmailSettingsTab() {
  const DEFAULTS = {
    reminder_enabled: false,
    reminder_checkpoints: [14, 7, 3, 1],
    reminder_subject: "Reminder: Your loss control survey is due in {{days_remaining}} day(s) — {{carrier_name}}",
    reminder_body: "Hello {{insured_name}},\n\nThis is a friendly reminder that your loss control survey for {{property_address}} is due on {{due_date}}.\n\nPlease complete your survey at your earliest convenience:\n{{survey_url}}\n\nIf you have already completed the survey, thank you — no further action is needed.\n\n{{carrier_name}} Loss Control Team",
    auto_send_report: false,
    report_email_subject: "Your Loss Control Report is Ready — {{carrier_name}}",
    report_email_body: "Hello {{insured_name}},\n\nYour loss control survey report for {{property_address}} has been completed and is attached to this email.\n\nPlease review the findings and recommendations. Contact your insurance representative if you have any questions.\n\n{{carrier_name}} Loss Control Team",
    from_name: "",
    reply_to_email: "",
    weekly_export_enabled: false,
    weekly_export_recipients: "",
  };
  const CHECKPOINTS = [
    { value: 14, label: "14 days before due" },
    { value: 7,  label: "7 days before due" },
    { value: 3,  label: "3 days before due" },
    { value: 1,  label: "24 hours before due" },
  ];
  const VARS_REMINDER = ["{{insured_name}}", "{{carrier_name}}", "{{due_date}}", "{{survey_url}}", "{{days_remaining}}", "{{property_address}}"];
  const VARS_REPORT   = ["{{insured_name}}", "{{carrier_name}}", "{{property_address}}", "{{inspection_types}}"];

  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [sendNowResult, setSendNowResult] = useState(null);

  useEffect(() => {
    api.get("/carrier/email-settings")
      .then(r => setForm({ ...DEFAULTS, ...r.data }))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const toggleCheckpoint = (val) => {
    const cur = form.reminder_checkpoints || [];
    setForm({ ...form, reminder_checkpoints: cur.includes(val) ? cur.filter(c => c !== val) : [...cur, val] });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/carrier/email-settings", form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const Toggle = ({ checked, onChange, testid }) => (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className={`relative inline-flex w-11 h-6 rounded-full transition-colors ${checked ? "bg-navy" : "bg-slate-300"}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  );

  const VarHint = ({ vars }) => (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {vars.map(v => (
        <code key={v} className="px-1.5 py-0.5 bg-navy/5 text-navy text-xs rounded font-mono border border-navy/10">{v}</code>
      ))}
    </div>
  );

  if (loading) return <div className="py-10 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-navy" /></div>;

  return (
    <div className="space-y-8" data-testid="email-settings-tab">
      {/* Section 1: Survey Reminders */}
      <div className="space-y-5">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <h3 className="font-heading font-semibold text-slate-800">Survey Reminders</h3>
            <p className="text-xs text-slate-400 mt-0.5">Automated emails sent to the insured before their survey due date</p>
          </div>
          <Toggle checked={form.reminder_enabled} onChange={v => setForm({ ...form, reminder_enabled: v })} testid="toggle-reminders" />
        </div>

        <div className={`space-y-5 transition-opacity ${form.reminder_enabled ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
          {/* Sender config */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">From Name <span className="text-slate-400 font-normal">(optional)</span></label>
              <input data-testid="from-name-input" value={form.from_name}
                onChange={e => setForm({ ...form, from_name: e.target.value })}
                placeholder="e.g. Acme Insurance Loss Control"
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
              <p className="text-xs text-slate-400 mt-1">Defaults to your carrier name</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Reply-To Email <span className="text-slate-400 font-normal">(optional)</span></label>
              <input data-testid="reply-to-input" type="email" value={form.reply_to_email}
                onChange={e => setForm({ ...form, reply_to_email: e.target.value })}
                placeholder="losscontrol@company.com"
                className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
              <p className="text-xs text-slate-400 mt-1">Insured can reply to this address</p>
            </div>
          </div>

          {/* Checkpoints */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Send reminders at</label>
            <div className="flex flex-wrap gap-3">
              {CHECKPOINTS.map(cp => (
                <label key={cp.value} className="flex items-center gap-2 cursor-pointer select-none"
                  data-testid={`checkpoint-${cp.value}`}>
                  <input type="checkbox" checked={(form.reminder_checkpoints || []).includes(cp.value)}
                    onChange={() => toggleCheckpoint(cp.value)}
                    className="w-4 h-4 rounded border-slate-300 text-navy accent-navy" />
                  <span className="text-sm text-slate-600">{cp.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Reminder Subject</label>
            <input data-testid="reminder-subject-input" value={form.reminder_subject}
              onChange={e => setForm({ ...form, reminder_subject: e.target.value })}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
            <VarHint vars={VARS_REMINDER} />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Reminder Body</label>
            <textarea data-testid="reminder-body-input" value={form.reminder_body} rows={7}
              onChange={e => setForm({ ...form, reminder_body: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy resize-y" />
            <p className="text-xs text-slate-400 mt-1">Use two blank lines (\\n\\n) between paragraphs</p>
            <VarHint vars={VARS_REMINDER} />
          </div>
        </div>
      </div>

      {/* Section 2: Report Delivery */}
      <div className="space-y-5 border-t border-slate-100 pt-6">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <h3 className="font-heading font-semibold text-slate-800">Report Delivery</h3>
            <p className="text-xs text-slate-400 mt-0.5">Email the PDF report to the insured when the underwriter finalizes</p>
          </div>
          <Toggle checked={form.auto_send_report} onChange={v => setForm({ ...form, auto_send_report: v })} testid="toggle-auto-report" />
        </div>

        <div className={`space-y-5 transition-opacity ${form.auto_send_report ? "opacity-100" : "opacity-70"}`}>
          <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700 leading-relaxed">
              <strong>Auto-send</strong> — when all inspections in a multi-line order are finalized, the consolidated PDF is emailed automatically. For single-line orders, it's sent when that inspection is finalized. Underwriters can also send manually at any time from the Report view.
            </p>
          </div>

          {/* Report subject */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Report Email Subject</label>
            <input data-testid="report-subject-input" value={form.report_email_subject}
              onChange={e => setForm({ ...form, report_email_subject: e.target.value })}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
            <VarHint vars={VARS_REPORT} />
          </div>

          {/* Report body */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Report Email Body</label>
            <textarea data-testid="report-body-input" value={form.report_email_body} rows={6}
              onChange={e => setForm({ ...form, report_email_body: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy resize-y" />
            <VarHint vars={VARS_REPORT} />
          </div>
        </div>
      </div>

      {/* Section 3: Weekly CSV Snapshot */}
      <div className="space-y-4 border-t border-slate-100 pt-6">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <h3 className="font-heading font-semibold text-slate-800">Weekly CSV Snapshot</h3>
            <p className="text-xs text-slate-400 mt-0.5">Every Monday, email a CSV export of orders, findings, and recommendation statuses — no login needed</p>
          </div>
          <Toggle checked={form.weekly_export_enabled} onChange={v => setForm({ ...form, weekly_export_enabled: v })} testid="toggle-weekly-export" />
        </div>
        <div className={`space-y-3 transition-opacity ${form.weekly_export_enabled ? "opacity-100" : "opacity-70"}`}>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Recipients <span className="text-slate-400 font-normal">(comma-separated — defaults to carrier admins if blank)</span></label>
            <input data-testid="weekly-export-recipients-input" value={form.weekly_export_recipients}
              onChange={e => setForm({ ...form, weekly_export_recipients: e.target.value })}
              placeholder="reporting@yourcompany.com, ops@yourcompany.com"
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
          </div>
          <div className="flex items-center gap-3">
            <button type="button" data-testid="send-export-now-btn" disabled={sendingNow}
              onClick={async () => {
                setSendingNow(true); setSendNowResult(null);
                try {
                  const res = await api.post("/carrier/email-settings/send-export-now");
                  setSendNowResult({ ok: true, msg: `Sent to ${res.data.recipients.join(", ")}` });
                } catch (e) {
                  setSendNowResult({ ok: false, msg: e.response?.data?.detail || "Failed to send" });
                }
                setSendingNow(false);
              }}
              className="flex items-center gap-2 px-4 py-2 border border-navy/30 text-navy rounded-lg text-sm font-medium hover:bg-navy/5 transition-colors disabled:opacity-50">
              {sendingNow ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {sendingNow ? "Sending…" : "Send Snapshot Now"}
            </button>
            {sendNowResult && (
              <span className={`text-sm font-medium ${sendNowResult.ok ? "text-green-600" : "text-red-600"}`} data-testid="send-now-result">
                {sendNowResult.msg}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
        <button onClick={handleSave} disabled={saving} data-testid="save-email-settings-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy/90 transition-colors disabled:opacity-60">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving..." : "Save Email Settings"}
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-green-600 text-sm font-medium" data-testid="save-success">
            <CheckCircle className="w-4 h-4" /> Saved successfully
          </span>
        )}
      </div>
    </div>
  );
}
